// ./src/utils/api/OrganizerFollowerService.ts
//
// ROADMAP B24 item 5 — the host's list of who follows an organizer page by email (eventsmagic-api
// OrganizerFollowerCore). Read-only: followers are written only by the visitors themselves (sign up, confirm,
// unsubscribe). One page of the caller's own account per read — the server refuses anything else by listing nothing.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { ListAllOptions, listAllPages } from "./listAllPages";

/** Where a follower is. Mirrors `FOLLOWER_STATE` in the server's `boundary/organizerFollow.ts` — extend both together. */
export const FOLLOWER_STATE = {
  /** Confirmed, not unsubscribed — told about new events. */
  FOLLOWING: "following",
  /** Signed up, never confirmed — never emailed about events. */
  PENDING: "pending",
  UNSUBSCRIBED: "unsubscribed",
} as const;

export type FollowerState = (typeof FOLLOWER_STATE)[keyof typeof FOLLOWER_STATE];

export interface OrganizerFollower {
  id?: string;
  organizerProfileId?: string;
  /** The contact (address book) who follows — their name and email are the contact's. */
  personId?: string | null;
  email?: string;
  name?: string | null;
  followerState?: FollowerState | string;
  /** ISO UTC. */
  signedUpAt?: string | null;
  confirmedAt?: string | null;
  unsubscribedAt?: string | null;
  /** Where the confirmation came from — the proof of consent. */
  consentIp?: string | null;
}

export interface OrganizerFollowerFilter {
  /** REQUIRED — one organizer page of the caller's own account. */
  organizerProfileId: string[];
  /** Omit for every state. */
  followerState?: FollowerState;
  searchKeyword?: string;
}

const FIELDS = `
  id
  organizerProfileId
  personId
  email
  name
  followerState
  signedUpAt
  confirmedAt
  unsubscribedAt
  consentIp
`;

const LIST_QUERY = gql`
  query OrganizerFollowerList($filter: OrganizerFollowerFilter, $params: PaginationAndSorting) {
    organizerFollowerList(filter: $filter, params: $params) {
      code
      total
      errors { name errors warnings }
      data { ${FIELDS} }
    }
  }
`;

export class OrganizerFollowerService {
  /** One page, newest sign-up first by default; `total` is the whole filtered set. Always a fresh read. */
  static async list(filter?: OrganizerFollowerFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(
      LIST_QUERY,
      { filter: filter || {}, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } },
      { fetchPolicy: "network-only" },
    );
  }

  /** The WHOLE filtered list in one read — the CSV export (it refuses past 50,000 rows rather than cutting). */
  static async listAll(filter: OrganizerFollowerFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<OrganizerFollower>(OrganizerFollowerService.list, filter, sortBy, opts);
  }
}
